<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useOpsStore } from '@/stores/ops'
import { useTeamsStore } from '@/stores/teams'
import { useAssignmentsStore } from '@/stores/assignments'
import { usePersonnelStore } from '@/stores/personnel'
import { useToastStore } from '@/stores/toast'
import { usePermissionsStore } from '@/stores/permissions'
import { useCaltopoStore } from '@/stores/caltopo'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import { useDragState } from '@/composables/useDragState'
import { TeamStatus, AssignmentStatus } from '@/types'
import type { Team } from '@/types'
import AssignmentCard from '@/components/assignments/AssignmentCard.vue'
import TeamDragCard from '@/components/assignments/TeamDragCard.vue'
import TeamDetailDialog from '@/components/teams/TeamDetailDialog.vue'
import AssignmentDetailDialog from '@/components/assignments/AssignmentDetailDialog.vue'
import CreateAssignmentDialog from '@/components/assignments/CreateAssignmentDialog.vue'
import CalTopoPopover from '@/components/assignments/CalTopoPopover.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosSelect from '@/components/ui/FosSelect.vue'
import PermissionGuard from '@/components/ui/PermissionGuard.vue'

import type { Assignment } from '@/types'
import draggable from 'vuedraggable'

const route = useRoute()
const incidentId = route.params.id as string

const opsStore = useOpsStore()
const teamsStore = useTeamsStore()
const assignmentsStore = useAssignmentsStore()
const personnelStore = usePersonnelStore()
const toast = useToastStore()
const permissionsStore = usePermissionsStore()
const caltopoStore = useCaltopoStore()
const { opRole } = useCurrentOpRole()
const { startDrag, endDrag, isDragging } = useDragState()

const canEdit = computed(() => permissionsStore.can('assignment.create', opRole.value))
const canDebrief = computed(() => permissionsStore.can('assignment.debrief', opRole.value))

// ── Stores subscription ────────────────────────────────────────────────────

watch(
  () => opsStore.selectedOpId,
  (opId) => {
    if (opId) {
      assignmentsStore.subscribeToAssignments(incidentId, opId)
    }
  },
  { immediate: true },
)

// ── CalTopo write-back ─────────────────────────────────────────────────────

let _writeBackTimer: ReturnType<typeof setTimeout> | null = null

onUnmounted(() => { if (saveTimer) clearTimeout(saveTimer) })

async function handleCaltopoConnected(payload: {
  mapId: string
  mapName: string
  caltopoOpId: string | null
  pollIntervalMs: number
  syncData: unknown
}) {
  console.log('[AssignmentsView] handleCaltopoConnected — payload:', payload)
  const op = opsStore.selectedOp
  if (!op) return
  await opsStore.updateOpCaltopoLink(incidentId, op.op_id, payload.mapId, payload.mapName, payload.caltopoOpId, payload.pollIntervalMs)
}

function _triggerWriteBack(assignmentId: string, changedFields: Partial<Assignment>) {
  const op = opsStore.selectedOp
  if (!op?.caltopo_map_id) return
  const assignment = assignmentsStore.assignments.find((a) => a.assignment_id === assignmentId)
  if (!assignment?.caltopo_assignment_id) return

  // Debounce: rapid field edits (e.g. multiple blurs in quick succession) fire a single write-back
  if (_writeBackTimer !== null) clearTimeout(_writeBackTimer)
  _writeBackTimer = setTimeout(() => {
    _writeBackTimer = null
    caltopoStore.writeBack(assignment.caltopo_assignment_id!, op.caltopo_map_id!, changedFields)
  }, 1500)
}

function _triggerWriteBackImmediate(assignmentId: string, changedFields: Partial<Assignment>) {
  // For discrete actions (status changes) — skip the debounce
  if (_writeBackTimer !== null) {
    clearTimeout(_writeBackTimer)
    _writeBackTimer = null
  }
  const op = opsStore.selectedOp
  if (!op?.caltopo_map_id) return
  const assignment = assignmentsStore.assignments.find((a) => a.assignment_id === assignmentId)
  if (assignment?.caltopo_assignment_id) {
    caltopoStore.writeBack(assignment.caltopo_assignment_id, op.caltopo_map_id, changedFields)
  }
}

// ── Panel resize ───────────────────────────────────────────────────────────

const PANEL_WIDTH_KEY = 'fos_personnel_panel_width'
const MIN_PANEL_WIDTH = 285
const MIN_ASSIGNMENTS_WIDTH = 320

const stored = parseInt(localStorage.getItem(PANEL_WIDTH_KEY) ?? '', 10)
const panelWidth = ref(isNaN(stored) ? 232 : stored)

let saveTimer: ReturnType<typeof setTimeout> | null = null

function savePanelWidth() {
  if (saveTimer) clearTimeout(saveTimer)
  saveTimer = setTimeout(() => {
    localStorage.setItem(PANEL_WIDTH_KEY, String(panelWidth.value))
  }, 100)
}

function onResizeMouseDown(e: MouseEvent) {
  e.preventDefault()
  const startX = e.clientX
  const startWidth = panelWidth.value

  function onMouseMove(ev: MouseEvent) {
    const delta = ev.clientX - startX
    const maxWidth = window.innerWidth - MIN_ASSIGNMENTS_WIDTH - 5
    panelWidth.value = Math.max(MIN_PANEL_WIDTH, Math.min(startWidth + delta, maxWidth))
    savePanelWidth()
  }

  function onMouseUp() {
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
  }

  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}

// ── Teams left panel ───────────────────────────────────────────────────────

const teamSearch = ref('')
const collapsed = ref({
  awaiting: false,
  assigned: true,
  other: true,
})

const teamSortField = ref<'name' | 'status' | 'members'>('name')
const teamSortDir = ref<'asc' | 'desc'>('asc')
const teamStatusFilter = ref<string | null>(null)

function toggleTeamSort(field: typeof teamSortField.value) {
  if (teamSortField.value === field) {
    teamSortDir.value = teamSortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    teamSortField.value = field
    teamSortDir.value = 'asc'
  }
}

const TEAM_STATUS_ORDER: Record<string, number> = {
  [TeamStatus.Emergency]: 0,
  [TeamStatus.OnScene]: 1,
  [TeamStatus.Enroute]: 2,
  [TeamStatus.Assigned]: 3,
  [TeamStatus.RTB]: 4,
  [TeamStatus.AwaitingAssignment]: 5,
  [TeamStatus.Building]: 6,
  [TeamStatus.Returned]: 7,
}

const teamStatusFilterOptions = computed(() => [
  { label: 'All', value: 'all' },
  ...Object.values(TeamStatus).map((s) => ({ label: s, value: s })),
])

const filteredTeams = computed(() => {
  const q = teamSearch.value.trim().toLowerCase()
  let result = teamsStore.teams.filter((t) => {
    if (t.team_type === 'OVERHEAD') return false
    if (q && !t.team_identifier.toLowerCase().includes(q)) return false
    if (teamStatusFilter.value && t.team_status !== teamStatusFilter.value) return false
    return true
  })

  result = [...result].sort((a, b) => {
    let cmp = 0
    if (teamSortField.value === 'name') {
      cmp = a.team_identifier.localeCompare(b.team_identifier)
    } else if (teamSortField.value === 'status') {
      cmp = (TEAM_STATUS_ORDER[a.team_status] ?? 9) - (TEAM_STATUS_ORDER[b.team_status] ?? 9)
    } else if (teamSortField.value === 'members') {
      const countA = personnelStore.roster.filter(p => p.team_id === a.team_id && p.time_out === null).length
      const countB = personnelStore.roster.filter(p => p.team_id === b.team_id && p.time_out === null).length
      cmp = countA - countB
    }
    return teamSortDir.value === 'asc' ? cmp : -cmp
  })

  return result
})

const groupedTeams = computed(() => ({
  building: filteredTeams.value.filter((p) => p.team_status === TeamStatus.Building),
  awaiting: filteredTeams.value.filter((p) => p.team_status === TeamStatus.AwaitingAssignment),
  assigned: filteredTeams.value.filter((p) => p.team_status === TeamStatus.Assigned),
  enroute: filteredTeams.value.filter((p) => p.team_status === TeamStatus.Enroute),
  onScene: filteredTeams.value.filter((p) => p.team_status === TeamStatus.OnScene),
  rtb: filteredTeams.value.filter((p) => p.team_status === TeamStatus.RTB),
  returned: filteredTeams.value.filter((p) => p.team_status === TeamStatus.Returned),
  emergency: filteredTeams.value.filter((p) => p.team_status === TeamStatus.Emergency),
}))

// ── Assignment search + filter/sort ────────────────────────────────────────

const assignmentSearch = ref('')
const filterResourceType = ref<Assignment['resource_type']>(null)
const sortField = ref<'name' | 'priority_order' | 'status'>('name')
const sortDir = ref<'asc' | 'desc'>('asc')

function toggleSort(field: typeof sortField.value) {
  if (sortField.value === field) {
    if (field !== 'priority_order') {
      sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
    }
  } else {
    sortField.value = field
    sortDir.value = 'asc'
  }
}

const sortOptions = [
  ['name', 'Name'],
  ['status', 'Status'],
  ['priority_order', 'Priority'],
] as const

const STATUS_ORDER: Record<string, number> = {
  [AssignmentStatus.InProgress]: 0,
  [AssignmentStatus.NotStarted]: 1,
  [AssignmentStatus.Draft]: 2,
  [AssignmentStatus.Completed]: 3,
}

const filteredAssignments = computed(() => {
  const q = assignmentSearch.value.trim().toLowerCase()
  let result = assignmentsStore.assignments.filter((a) => {
    if (q) {
      const matchesName = (a.assignment_name ?? '').toLowerCase().includes(q)
      const matchesDesc = (a.description ?? '').toLowerCase().includes(q)
      const matchesTeam = assignmentsStore.getAssignedTeamIds(a.assignment_id).some(
        (id) => (teamsStore.teams.find((t) => t.team_id === id)?.team_identifier ?? '').toLowerCase().includes(q)
      )
      if (!matchesName && !matchesDesc && !matchesTeam) return false
    }
    if (filterResourceType.value && a.resource_type !== filterResourceType.value) return false
    return true
  })

  result = [...result].sort((a, b) => {
    let cmp = 0
    if (sortField.value === 'name') {
      cmp = (a.assignment_name ?? '').localeCompare(b.assignment_name ?? '')
    } else if (sortField.value === 'status') {
      cmp = (STATUS_ORDER[a.assignment_status] ?? 3) - (STATUS_ORDER[b.assignment_status] ?? 3)
    } else if (sortField.value === 'priority_order') {
      const aOrder = a.sort_order ?? Number.MAX_SAFE_INTEGER
      const bOrder = b.sort_order ?? Number.MAX_SAFE_INTEGER
      cmp = aOrder - bOrder
    }
    return sortDir.value === 'asc' ? cmp : -cmp
  })

  return result
})

// ── Priority reorder ───────────────────────────────────────────────────────

const draggableAssignments = ref<typeof filteredAssignments.value>([])

watch(
  filteredAssignments,
  (val) => {
    draggableAssignments.value = [...val]
  },
  { immediate: true },
)

async function handlePriorityReorder() {
  const opId = opsStore.selectedOpId
  if (!opId) return
  try {
    await assignmentsStore.reorderAssignments(incidentId, opId, draggableAssignments.value)
  } catch (e) {
    toast.add(String(e), 'error', 4000)
  }
}

// ── Drag-and-drop ──────────────────────────────────────────────────────────

async function handleDrop(assignmentId: string, teamId: string) {
  const opId = opsStore.selectedOpId
  if (!opId) return
  try {
    await assignmentsStore.assignTeam(incidentId, opId, assignmentId, teamId)
  } catch (e) {
    toast.add(String(e), 'warning', 4000)
  }
}

async function handleDetach(assignmentId: string, teamId: string) {
  const opId = opsStore.selectedOpId
  if (!opId) return
  try {
    await assignmentsStore.detachTeam(incidentId, opId, assignmentId, teamId)
  } catch (e) {
    toast.add(String(e), 'error', 4000)
  }
}

async function handleStatusChange(assignmentId: string, status: AssignmentStatus) {
  const opId = opsStore.selectedOpId
  if (!opId) return
  try {
    await assignmentsStore.setAssignmentStatus(incidentId, opId, assignmentId, status)
    _triggerWriteBackImmediate(assignmentId, { assignment_status: status })
  } catch (e) {
    toast.add(String(e), 'error', 4000)
  }
}

// ── Team detail dialog ─────────────────────────────────────────────────────

const detailTeam = ref<Team | null>(null)
const detailDialogVisible = computed({
  get: () => detailTeam.value !== null,
  set: (v) => { if (!v) detailTeam.value = null },
})

// ── Assignment detail dialog ────────────────────────────────────────────────

const selectedAssignment = ref<Assignment | null>(null)

function openDetail(assignment: Assignment) {
  selectedAssignment.value = assignment
}

async function handleDialogAssign(assignmentId: string, teamId: string) {
  const opId = opsStore.selectedOpId
  if (!opId) return
  try {
    await assignmentsStore.assignTeam(incidentId, opId, assignmentId, teamId)
  } catch (e) {
    toast.add(String(e), 'warning', 4000)
  }
}

async function handleDialogDetach(assignmentId: string, teamId: string) {
  const opId = opsStore.selectedOpId
  if (!opId) return
  try {
    await assignmentsStore.detachTeam(incidentId, opId, assignmentId, teamId)
  } catch (e) {
    toast.add(String(e), 'error', 4000)
  }
}

function getAssignedTeams(assignmentId: string): Team[] {
  return assignmentsStore.getAssignedTeamIds(assignmentId)
    .map((id) => teamsStore.teams.find((t) => t.team_id === id))
    .filter((t): t is Team => t !== undefined)
}

async function handleDialogSave(
  assignmentId: string,
  fields: Parameters<typeof assignmentsStore.updateAssignment>[3],
) {
  const opId = opsStore.selectedOpId
  if (!opId) return
  try {
    await assignmentsStore.updateAssignment(incidentId, opId, assignmentId, fields)
    _triggerWriteBack(assignmentId, fields)
  } catch (e) {
    toast.add(String(e), 'error', 4000)
  }
}

// ── Create assignment ──────────────────────────────────────────────────────

const showCreateDialog = ref(false)

async function handleCreate(data: Parameters<typeof assignmentsStore.createAssignment>[2]) {
  const opId = opsStore.selectedOpId
  if (!opId) return
  try {
    await assignmentsStore.createAssignment(incidentId, opId, data)
    showCreateDialog.value = false
  } catch (e) {
    toast.add(String(e), 'error', 4000)
  }
}

</script>

<template>
  <div class="h-full flex flex-col overflow-hidden">
    <!-- Header bar -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
      <div>
        <h2 class="font-headline font-black text-lg uppercase tracking-tight text-on-surface">Assignments</h2>
        <p class="font-mono text-xs text-outline mt-0.5">{{ assignmentsStore.assignments.length }} assignment{{ assignmentsStore.assignments.length !== 1 ? 's' : '' }}</p>
      </div>

      <div class="flex items-center gap-2">
        <!-- CalTopo connection status / button -->
        <PermissionGuard v-if="opsStore.selectedOpId && opsStore.selectedOp" action="assignment.create" :role="opRole">
          <CalTopoPopover
            :incident-id="incidentId"
            :op-id="opsStore.selectedOp.op_id"
            :op-number="opsStore.selectedOp.op_number"
            :connected="!!opsStore.selectedOp.caltopo_map_id"
            :map-name="opsStore.selectedOp.caltopo_map_name"
            @connected="handleCaltopoConnected"
            @toggle-pause="caltopoStore.togglePause()"
            @manual-refresh="caltopoStore.manualSync(true)"
          />
        </PermissionGuard>

        <!-- New assignment -->
        <PermissionGuard v-if="opsStore.selectedOpId" action="assignment.create" :role="opRole">
          <button
            type="button"
            class="flex items-center justify-center w-8 h-8 text-outline hover:text-on-surface transition-colors"
            title="New Assignment"
            @click="showCreateDialog = true"
          >
            <span class="material-symbols-outlined text-[20px]">add</span>
          </button>
        </PermissionGuard>
      </div>
    </div>

    <!-- No OP selected -->
    <div
      v-if="!opsStore.selectedOpId"
      class="flex flex-1 items-center justify-center bg-surface-container-low px-6 py-4"
    >
      <p class="text-sm text-outline font-mono">No operational period selected.</p>
    </div>

    <!-- Main layout -->
    <div v-else class="flex flex-1 overflow-hidden px-6 py-4">
      <!-- Left panel: teams by status -->
      <div
        class="shrink-0 overflow-y-auto"
        :style="{ width: `${panelWidth}px`, minWidth: `${MIN_PANEL_WIDTH}px` }"
      >
        <div class="@container flex flex-col gap-3 overflow-hidden">
          <!-- Team search -->
          <div class="flex shrink-0 border border-border rounded overflow-visible">
            <FosInput
              v-model="teamSearch"
              placeholder="Search teams…"
              class="flex-1 border-0 rounded-none"
            />
          </div>

          <!-- Team filter / sort bar -->
          <div class="flex items-center gap-3 shrink-0 flex-wrap border rounded  border-border bg-surface-container px-3 py-2">
            <!-- Sort buttons -->
            <button
              v-for="[field, label] in ([['name', 'Name'], ['status', 'Status'], ['members', 'Size']] as const)"
              :key="field"
              type="button"
              class="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono transition-colors"
              :class="teamSortField === field ? 'bg-surface-container-highest text-on-surface' : 'text-outline hover:text-on-surface-variant'"
              @click="toggleTeamSort(field)"
            >
              {{ label }}
              <span v-if="teamSortField === field" class="material-symbols-outlined text-[10px]">
                {{ teamSortDir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
              </span>
            </button>

            <span class="w-px h-3 bg-border shrink-0" />

            <!-- Status filter -->
            <FosSelect
              :model-value="teamStatusFilter ?? 'all'"
              :options="teamStatusFilterOptions"
              trigger-class="px-2 py-0.5 text-[10px] font-mono bg-transparent text-outline hover:text-on-surface-variant transition-colors"
              icon-class="material-symbols-outlined text-current text-[10px] ml-0.5 opacity-60"
              @update:model-value="teamStatusFilter = $event === 'all' ? null : $event"
            />

            <!-- Clear filter -->
            <button
              v-if="teamStatusFilter"
              type="button"
              class="ml-auto text-[10px] font-mono text-outline hover:text-error transition-colors"
              title="Clear filter"
              @click="teamStatusFilter = null"
            >
              <span class="material-symbols-outlined text-[11px]">close</span>
            </button>
          </div>
          
          

          <div class="flex flex-col gap-1 shrink-0">
            <template v-if="!collapsed.awaiting"> <!-- For some god forsaken reason taking this useless v-if out makes the whole team list dissapear -->
                <draggable
                  :model-value="filteredTeams"
                  :group="{ name: 'team' }"
                  item-key="team_id"
                  ghost-class="drag-ghost--card"
                  drag-class="drag-cursor--chip"
                  :force-fallback="true"
                  :fallback-on-body="true"
                  class="flex flex-col gap-1.5 rounded-sm min-h-[36px] border border-dashed transition-colors"
                  :class="isDragging ? 'border-primary/50 bg-primary/5' : 'border-transparent'"
                  @start="startDrag"
                  @end="endDrag"
                >
                  <template #item="{ element }">
                    <TeamDragCard :team="element" :has-debriefable="false"  />
                  </template>
                </draggable>
            </template>
          </div>
        </div>
      </div>

      <!-- Resize handle -->
      <div
        class="w-[5px] shrink-0 self-stretch cursor-col-resize bg-border hover:bg-primary transition-colors mx-1"
        @mousedown="onResizeMouseDown"
      />

      <!-- Right area: assignment grid or empty states -->
      <div class="flex-1 flex flex-col gap-3 overflow-hidden">
        <!-- Assignment search -->
        <div class="flex shrink-0 border border-border rounded overflow-visible">
          <FosInput
            v-model="assignmentSearch"
            placeholder="Search by name, description, or team…"
            class="flex-1 border-0 rounded-none"
          />
        </div>

        <!-- Filter / sort bar -->
        <div class="flex items-center gap-3 shrink-0 flex-wrap border border-border rounded bg-surface-container px-3 py-2">
          <!-- Sort buttons -->
          <div class="flex items-center gap-1.5">
            <span class="text-[10px] font-label uppercase tracking-widest text-outline">Sort</span>
            <template v-for="opt in sortOptions" :key="opt[0]">
              <button
                type="button"
                class="inline-flex items-center gap-0.5 px-2 py-0.5 text-[11px] font-mono transition-colors"
                :class="sortField === opt[0] ? 'bg-surface-container-highest text-on-surface' : 'text-outline hover:text-on-surface-variant'"
                @click="toggleSort(opt[0])"
              >
                {{ opt[1] }}
                <span v-if="sortField === opt[0]" class="material-symbols-outlined text-[11px]">
                  {{ sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </span>
              </button>
            </template>
          </div>

          <span class="w-px h-4 bg-border shrink-0" />

          <!-- Resource type filter -->
          <div class="flex items-center gap-1.5">
            <span class="text-[10px] font-label uppercase tracking-widest text-outline">Type</span>
            <button
              v-for="rt in (['GROUND', 'AIR', 'WATER', 'K9'] as const)"
              :key="rt"
              type="button"
              class="inline-flex items-center px-2 py-0.5 text-[11px] font-mono transition-colors"
              :class="filterResourceType === rt ? 'bg-surface-container-highest text-on-surface' : 'text-outline hover:text-on-surface-variant'"
              @click="filterResourceType = filterResourceType === rt ? null : rt"
            >
              {{ rt }}
            </button>
          </div>

        </div>

        <div class="flex-1 overflow-y-auto">
          <!-- No assignments yet -->
          <div
            v-if="assignmentsStore.assignments.length === 0"
            class="flex h-full flex-col items-center justify-center border border-dashed border-border bg-surface-container-low text-center p-8"
          >
            <span class="material-symbols-outlined text-[32px] text-outline mb-3">map</span>
            <p class="text-sm text-outline font-mono">No assignments in this operational period</p>
          </div>

          <!-- Assignment grid — priority reorder mode -->
          <draggable
            v-else-if="sortField === 'priority_order' && canEdit"
            v-model="draggableAssignments"
            item-key="assignment_id"
            ghost-class="drag-ghost--card"
            :force-fallback="true"
            :fallback-on-body="true"
            handle=".drag-handle"
            class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 content-start"
            @end="handlePriorityReorder"
          >
            <template #item="{ element: assignment }">
              <AssignmentCard
                :assignment="assignment"
                :assigned-teams="getAssignedTeams(assignment.assignment_id)"
                :can-edit="canEdit"
                :permission-role="opRole"
                :sortable="true"
                @drop-team="handleDrop(assignment.assignment_id, $event)"
                @drop-secondary="handleDrop(assignment.assignment_id, $event)"
                @detach-team="handleDetach(assignment.assignment_id, $event)"
                @status-change="handleStatusChange(assignment.assignment_id, $event)"
                @select="openDetail(assignment)"
              />
            </template>
          </draggable>

          <!-- Assignment grid — normal mode -->
          <div
            v-else
            class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 content-start"
          >
            <template v-if="filteredAssignments.length > 0">
              <AssignmentCard
                v-for="assignment in filteredAssignments"
                :key="assignment.assignment_id"
                :assignment="assignment"
                :assigned-teams="getAssignedTeams(assignment.assignment_id)"
                :can-edit="canEdit"
                :permission-role="opRole"
                @drop-team="handleDrop(assignment.assignment_id, $event)"
                @drop-secondary="handleDrop(assignment.assignment_id, $event)"
                @detach-team="handleDetach(assignment.assignment_id, $event)"
                @status-change="handleStatusChange(assignment.assignment_id, $event)"
                @select="openDetail(assignment)"
              />
            </template>
            <div v-else class="col-span-full flex flex-col items-center justify-center py-12 text-center">
              <span class="material-symbols-outlined text-[24px] text-outline mb-2">search</span>
              <p class="text-sm text-outline font-mono">No assignments match "{{ assignmentSearch }}"</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Assignment detail dialog -->
  <AssignmentDetailDialog
    v-if="selectedAssignment"
    :assignment="selectedAssignment"
    :assigned-teams="getAssignedTeams(selectedAssignment.assignment_id)"
    :teams="teamsStore.teams"
    :can-edit="canEdit"
    :permission-role="opRole"
    :caltopo-map-id="opsStore.selectedOp?.caltopo_map_id ?? null"
    @close="selectedAssignment = null"
    @assign-team="handleDialogAssign(selectedAssignment.assignment_id, $event)"
    @detach-team="handleDialogDetach(selectedAssignment.assignment_id, $event)"
    @status-change="handleStatusChange(selectedAssignment.assignment_id, $event)"
    @save="handleDialogSave(selectedAssignment.assignment_id, $event)"
  />

  <!-- Create assignment dialog -->
  <CreateAssignmentDialog
    v-model:open="showCreateDialog"
    @create="handleCreate"
  />

<TeamDetailDialog
    v-model:visible="detailDialogVisible"
    :team="detailTeam"
    :incident-id="incidentId"
    :op-id="opsStore.selectedOpId ?? ''"
  />
</template>
